import {
  applySettingsDoc,
  canPlayOnline,
  initialSettingsDoc,
  mergeProgress,
  mergeSettingsDocs,
  parseProgressDoc,
  parseSettingsDoc,
  progressDocFrom,
  sameValue,
  syncDocument,
  type ConsentMap,
  type SettingsDoc,
  type SyncOutcome,
  type SyncPorts,
} from '@il-colpo/shared/account';
import type { Progress } from '@il-colpo/shared/progress';
import { useProgressStore } from '../state/progressStore.ts';
import { useSettingsStore } from '../state/settingsStore.ts';
import { currentSettingsDoc, loadSyncMeta, saveSyncMeta } from './syncMeta.ts';
import type { AccountClient, DocumentKind } from './types.ts';

/**
 * Cross-save: the settings and the progress of a signed-in player follow them from device to
 * device. The rules (what merges how) live in `@il-colpo/shared/account`; this is the part that
 * knows the stores, the account service and when to run.
 *
 * It runs for a real account only (not a guest: nothing to come back to), once the terms and the
 * privacy notice are accepted (the data goes to the cloud), and not while a second factor is still
 * owed. It runs when the player signs in, when a setting or the progress changes (after a quiet
 * moment), when the page is shown again and when the connection comes back.
 */

export type SyncState = 'off' | 'needsConsent' | 'idle' | 'syncing' | 'ok' | 'error';

export interface SyncStatus {
  state: SyncState;
  /** When the last sync ended well (ms since the epoch). */
  at: number | null;
  /** Why the last one failed: `read`, `write`, `conflicts` or `unreadable`. */
  reason: string | null;
}

export interface CrossSaveOptions {
  /** The consents the player has given, as the app knows them now. */
  consents: () => ConsentMap | null;
  /** How long to wait after a change before saving it. */
  debounceMs?: number;
  /** Do not sync again on a page that is shown again within this time of the last sync. */
  minGapMs?: number;
  now?: () => number;
}

const same = (a: unknown, b: unknown): boolean => sameValue(a, b);

export class CrossSave {
  private current: SyncStatus = { state: 'off', at: null, reason: null };
  private readonly listeners = new Set<(s: SyncStatus) => void>();
  private readonly disposers: (() => void)[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running: Promise<SyncStatus> | null = null;
  private again = false;
  private readonly now: () => number;
  private readonly debounceMs: number;
  private readonly minGapMs: number;

  constructor(
    private readonly client: AccountClient,
    private readonly options: CrossSaveOptions,
  ) {
    this.now = options.now ?? Date.now;
    this.debounceMs = options.debounceMs ?? 3000;
    this.minGapMs = options.minGapMs ?? 60_000;
  }

  status(): SyncStatus {
    return this.current;
  }

  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private set(next: SyncStatus): void {
    this.current = next;
    for (const l of [...this.listeners]) l(next);
  }

  /** Why a sync cannot run now, or null when it can. */
  private blocker(): SyncState | null {
    const { status, user, needsSecondFactor } = this.client.snapshot();
    if (status !== 'signedIn' || !user || user.anonymous || needsSecondFactor) return 'off';
    const consents = this.options.consents();
    if (!consents || !canPlayOnline(consents)) return 'needsConsent';
    return null;
  }

  // ---- When to run ------------------------------------------------------------------------

  start(): void {
    if (this.disposers.length > 0) return;
    this.disposers.push(
      this.client.subscribe(() => {
        this.refreshEligibility();
      }),
      useSettingsStore.subscribe((state, previous) => {
        if (state.settings !== previous.settings) this.schedule();
      }),
      useProgressStore.subscribe((state, previous) => {
        if (state.progress !== previous.progress) this.schedule();
      }),
    );
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      const gap = this.now() - (this.current.at ?? 0);
      if (gap >= this.minGapMs) void this.syncNow();
    };
    const onOnline = (): void => {
      void this.syncNow();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    this.disposers.push(
      () => {
        document.removeEventListener('visibilitychange', onVisible);
      },
      () => {
        window.removeEventListener('online', onOnline);
      },
    );
    this.refreshEligibility();
  }

  stop(): void {
    for (const d of this.disposers.splice(0)) d();
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** The account or the consents changed: run if now allowed, and show why not if not. */
  refreshEligibility(): void {
    const blocker = this.blocker();
    if (blocker) {
      if (this.current.state !== blocker) this.set({ ...this.current, state: blocker });
      return;
    }
    if (this.current.state === 'off' || this.current.state === 'needsConsent') {
      void this.syncNow();
    }
  }

  private schedule(): void {
    if (this.blocker()) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.syncNow();
    }, this.debounceMs);
  }

  // ---- Running ----------------------------------------------------------------------------

  /** Syncs now; if a sync is already running, it runs once more when that one ends. */
  syncNow(): Promise<SyncStatus> {
    const blocker = this.blocker();
    if (blocker) {
      this.set({ ...this.current, state: blocker });
      return Promise.resolve(this.current);
    }
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = this.run().finally(() => {
      this.running = null;
      if (this.again) {
        this.again = false;
        void this.syncNow();
      }
    });
    return this.running;
  }

  /** The ports of one document: `wrap` is how a value is stored in the cloud. */
  private ports<T>(
    kind: DocumentKind,
    wrap: (value: T) => unknown,
    p: Pick<SyncPorts<T>, 'readLocal' | 'parse' | 'merge' | 'applyLocal'>,
  ): SyncPorts<T> {
    return {
      ...p,
      equal: same,
      readRemote: async () => {
        const r = await this.client.readDocument(kind);
        return r.ok ? { ok: true, remote: r.value } : { ok: false };
      },
      writeRemote: async (data, expectedVersion) => {
        const r = await this.client.saveDocument(kind, wrap(data), expectedVersion);
        if (r.ok) return { ok: true, version: r.value.version };
        return { ok: false, conflict: r.error.code === 'conflict' };
      },
    };
  }

  private async run(): Promise<SyncStatus> {
    this.set({ ...this.current, state: 'syncing' });
    try {
      this.adoptUser();
      const now = this.now;
      const outcomes: SyncOutcome[] = [];

      outcomes.push(
        await syncDocument<SettingsDoc>(
          this.ports<SettingsDoc>('settings', (doc) => doc, {
            readLocal: () => {
              const meta = loadSyncMeta();
              const doc = currentSettingsDoc(useSettingsStore.getState().settings, meta, now());
              saveSyncMeta({ ...meta, settings: doc });
              return doc;
            },
            parse: parseSettingsDoc,
            merge: (local, remote) => (remote ? mergeSettingsDocs(local, remote) : local),
            applyLocal: (merged) => {
              // The stamps are saved first, so that applying the values does not stamp them anew.
              saveSyncMeta({ ...loadSyncMeta(), settings: merged });
              useSettingsStore.getState().update((draft) => {
                Object.assign(draft, applySettingsDoc(draft, merged));
              });
            },
          }),
        ),
      );

      outcomes.push(
        await syncDocument<Progress>(
          this.ports<Progress>('progress', progressDocFrom, {
            readLocal: () => useProgressStore.getState().progress,
            parse: parseProgressDoc,
            merge: (local, remote) => (remote ? mergeProgress(local, remote) : local),
            applyLocal: (merged) => {
              useProgressStore.getState().setProgress(merged);
            },
          }),
        ),
      );

      const failed = outcomes.find((o) => o.status === 'failed');
      if (failed && failed.status === 'failed') {
        const next: SyncStatus = { state: 'error', at: this.current.at, reason: failed.reason };
        this.set(next);
        return next;
      }
      const next: SyncStatus = { state: 'ok', at: this.now(), reason: null };
      this.set(next);
      return next;
    } catch {
      const next: SyncStatus = { state: 'error', at: this.current.at, reason: 'read' };
      this.set(next);
      return next;
    }
  }

  /**
   * The first sync with an account other than the one this device last synced with: what is here
   * belongs to somebody else, and must not be merged into this account. The progress is cleared and
   * the settings, which carry no age, give way to the account's.
   */
  private adoptUser(): void {
    const id = this.client.snapshot().user?.id;
    if (!id) return;
    const meta = loadSyncMeta();
    if (meta.user === id) return;
    if (meta.user !== null) {
      useProgressStore.getState().reset();
      const settings = useSettingsStore.getState().settings;
      saveSyncMeta({ settings: initialSettingsDoc(settings, settings, this.now()), user: id });
      return;
    }
    saveSyncMeta({ ...meta, user: id });
  }
}

/** Wipes what cross-save remembers on this device (when a player asks to leave nothing behind). */
export function forgetSyncMeta(): void {
  saveSyncMeta({ settings: null, user: null });
}
